<script>
/**
 * @component TaskHealthConversationForm
 * @description Formulario específico para crear/editar HealthConversationTask.
 *              Análogo a TaskCheckinsForm. Maneja:
 *                - Selección de plantilla (dropdown que hidrata defaults)
 *                - Objetivo (textarea)
 *                - Criterios (chips editables)
 *                - Umbrales del semáforo (3 ranges)
 *                - Max turns
 *                - Toggle patientTriggerable (disabled "Próximamente")
 *                - Tab "Configuración IA" con prompts resueltos read-only
 *
 *              v-model: el objeto task que el padre (TaskDetail.vue) mantiene.
 *              Emite `input` con el task actualizado.
 *
 * @example <TaskHealthConversationForm v-model="task" />
 */
import healthConversationTemplateService from "@/services/health-conversation-template";
import { ALLOWED_MODELS } from "@/services/health-conversation-template";

/**
 * @description Render local de variables Mustache-like `{{key}}` para
 *              preview del prompt resuelto en el tab "Configuración IA".
 * @param {string} template
 * @param {Object} vars
 * @returns {string}
 */
function renderMustache(template, vars = {}) {
  if (!template) return "";
  return template.replace(/\{\{(\w+)\}\}/g, (_, k) =>
    vars[k] === undefined || vars[k] === null ? "" : String(vars[k])
  );
}

export default {
  name: "TaskHealthConversationForm",

  props: {
    value: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      activeTab: "general",
      templates: [],
      loadingTemplates: false,
      newCriterion: "",
    };
  },

  computed: {
    task: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    /**
     * @description Plantilla seleccionada (lookup por key).
     */
    selectedTemplate() {
      if (!this.task.templateKey) return null;
      return (
        this.templates.find((t) => t.key === this.task.templateKey) || null
      );
    },
    /**
     * @description Opciones del dropdown — solo plantillas activas.
     */
    templateOptions() {
      return this.templates.filter((t) => t.active !== false);
    },
    /**
     * @description Etiqueta legible del modelo conductor.
     */
    conductorModelLabel() {
      if (!this.selectedTemplate) return "";
      const m = ALLOWED_MODELS.find(
        (x) => x.value === this.selectedTemplate.conductorModel
      );
      return m ? m.label : this.selectedTemplate.conductorModel;
    },
    /**
     * @description Etiqueta legible del modelo evaluador.
     */
    evaluatorModelLabel() {
      if (!this.selectedTemplate) return "";
      const m = ALLOWED_MODELS.find(
        (x) => x.value === this.selectedTemplate.evaluatorModel
      );
      return m ? m.label : this.selectedTemplate.evaluatorModel;
    },
    /**
     * @description Prompt conductor resuelto con valores del form (preview).
     */
    resolvedConductorPrompt() {
      if (!this.selectedTemplate) return "";
      return renderMustache(this.selectedTemplate.conductorPromptTemplate, {
        objective: this.task.objective || "",
        criteria: (this.task.evaluationCriteria || []).join(", "),
        maxTurns: this.task.maxTurns || 5,
        turnsUsed: 0,
        transcript: "(transcript se renderiza al disparar la conversación)",
      });
    },
    /**
     * @description Prompt evaluador resuelto (preview).
     */
    resolvedEvaluatorPrompt() {
      if (!this.selectedTemplate) return "";
      return renderMustache(this.selectedTemplate.evaluatorPromptTemplate, {
        criteria: (this.task.evaluationCriteria || []).join(", "),
        thresholds: JSON.stringify(this.task.scoreThresholds || {}),
        transcript: "(transcript se renderiza al cierre)",
        closedReason: "ai_sufficient",
      });
    },
    /**
     * @description Cap superior del verde (slider izquierdo).
     */
    greenMax: {
      get() {
        return this.task.scoreThresholds?.green?.[1] || 3;
      },
      set(v) {
        this.updateThresholds(v, this.yellowMax);
      },
    },
    /**
     * @description Cap superior del amarillo (slider derecho).
     */
    yellowMax: {
      get() {
        return this.task.scoreThresholds?.yellow?.[1] || 7;
      },
      set(v) {
        this.updateThresholds(this.greenMax, v);
      },
    },
  },

  mounted() {
    this.loadTemplates();
  },

  methods: {
    /**
     * @description Carga plantillas activas para el dropdown.
     */
    async loadTemplates() {
      this.loadingTemplates = true;
      try {
        const { data } = await healthConversationTemplateService.list({
          active: "true",
        });
        this.templates = Array.isArray(data) ? data : [];
      } catch (e) {
        this.$message.error("No se pudieron cargar las plantillas");
      } finally {
        this.loadingTemplates = false;
      }
    },
    /**
     * @description Cuando el médico elige plantilla, hidrata los defaults
     *              SOLO si los campos del task están vacíos. Si ya editó algo,
     *              respetamos su edición.
     */
    onTemplateChange(key) {
      const template = this.templates.find((t) => t.key === key);
      if (!template) return;
      const next = { ...this.task, templateKey: key };
      if (!next.objective) next.objective = template.defaultObjective;
      if (!next.evaluationCriteria || next.evaluationCriteria.length === 0) {
        next.evaluationCriteria = [...(template.defaultCriteria || [])];
      }
      if (!next.scoreThresholds || !next.scoreThresholds.green) {
        next.scoreThresholds = {
          green: [...(template.defaultThresholds?.green || [1, 3])],
          yellow: [...(template.defaultThresholds?.yellow || [4, 7])],
          red: [...(template.defaultThresholds?.red || [8, 10])],
        };
      }
      if (!next.maxTurns) next.maxTurns = template.defaultMaxTurns || 5;
      if (!next.name) next.name = template.name;
      this.task = next;
    },
    /**
     * @description Re-armar scoreThresholds garantizando que cubran [1,10].
     *              Recibe el cap superior del verde y del amarillo.
     */
    updateThresholds(greenMax, yellowMax) {
      const gMax = Math.max(1, Math.min(greenMax, yellowMax - 1));
      const yMax = Math.max(gMax + 1, Math.min(yellowMax, 9));
      this.task = {
        ...this.task,
        scoreThresholds: {
          green: [1, gMax],
          yellow: [gMax + 1, yMax],
          red: [yMax + 1, 10],
        },
      };
    },
    /**
     * @description Agrega un criterio (chip).
     */
    addCriterion() {
      const c = (this.newCriterion || "").trim();
      if (!c) return;
      const exists = (this.task.evaluationCriteria || []).some(
        (x) => x.toLowerCase() === c.toLowerCase()
      );
      if (exists) {
        this.newCriterion = "";
        return;
      }
      this.task = {
        ...this.task,
        evaluationCriteria: [...(this.task.evaluationCriteria || []), c],
      };
      this.newCriterion = "";
    },
    /**
     * @description Remueve un criterio por índice.
     */
    removeCriterion(idx) {
      const next = [...(this.task.evaluationCriteria || [])];
      next.splice(idx, 1);
      this.task = { ...this.task, evaluationCriteria: next };
    },
  },
};
</script>

<template lang="pug">
  .task-health-conversation-form
    el-tabs(v-model="activeTab")
      //- ── TAB GENERAL ──
      el-tab-pane(label="General" name="general")
        el-form(label-position="top")
          //- Plantilla
          el-form-item(label="Plantilla base")
            el-select(
              :value="task.templateKey"
              placeholder="Elegí una plantilla"
              :loading="loadingTemplates"
              style="width: 100%"
              @change="onTemplateChange"
            )
              el-option(
                v-for="t in templateOptions"
                :key="t.key"
                :label="t.name"
                :value="t.key"
              )
            .form-help Plantilla armada por el equipo clínico. Editá los campos para ajustarla a tu caso.

          //- Nombre
          el-form-item(label="Nombre de la tarea")
            el-input(
              :value="task.name"
              @input="task = { ...task, name: $event }"
            )

          //- Objetivo
          el-form-item(label="Objetivo de la conversación")
            el-input(
              type="textarea"
              :rows="3"
              :value="task.objective"
              @input="task = { ...task, objective: $event }"
            )
            .form-help La IA usa este texto para conducir la conversación.

          //- Criterios (chips)
          el-form-item(label="Criterios a evaluar")
            .criteria-chips
              el-tag(
                v-for="(c, idx) in task.evaluationCriteria || []"
                :key="`crit-${idx}`"
                closable
                :disable-transitions="false"
                @close="removeCriterion(idx)"
              ) {{ c }}
              el-input(
                v-model="newCriterion"
                placeholder="+ agregar"
                size="small"
                style="width: 140px"
                @keyup.enter.native="addCriterion"
                @blur="addCriterion"
              )

          //- Umbrales
          el-form-item(label="Umbrales del semáforo (score 1-10)")
            .thresholds-row
              .thresholds-row__col
                .thresholds-row__label Verde
                el-input-number(
                  :value="greenMax"
                  :min="1"
                  :max="8"
                  size="small"
                  @input="greenMax = $event"
                )
                .thresholds-row__hint hasta
              .thresholds-row__col
                .thresholds-row__label Amarillo
                el-input-number(
                  :value="yellowMax"
                  :min="2"
                  :max="9"
                  size="small"
                  @input="yellowMax = $event"
                )
                .thresholds-row__hint hasta
              .thresholds-row__col
                .thresholds-row__label Rojo
                .thresholds-row__readonly {{ (yellowMax || 7) + 1 }}–10
            .form-help Score 1 = óptimo / 10 = crítico. Editá los topes con cuidado.

          //- Max turns — cuántas preguntas puede hacer el bot antes de cerrar
          el-form-item(label="¿Cuántas preguntas puede hacerle el bot al paciente?")
            el-select(
              :value="task.maxTurns"
              style="width: 280px"
              @input="task = { ...task, maxTurns: $event }"
            )
              el-option(:value="3" label="Hasta 3 preguntas (corto)")
              el-option(:value="5" label="Hasta 5 preguntas (recomendado)")
              el-option(:value="7" label="Hasta 7 preguntas (extendido)")
            .form-help Cada turno es una pregunta del bot + la respuesta del paciente. Si la IA junta info suficiente antes, cierra sola; si llega al máximo y aún falta, cierra y evalúa con lo que tenga. Más preguntas = más datos pero más costo + más tiempo del paciente.

          //- Patient triggerable (DISABLED)
          el-form-item
            .patient-trigger-row
              el-switch(:value="false" disabled)
              .patient-trigger-row__text
                .patient-trigger-row__label
                  | El paciente puede iniciar esta conversación desde WhatsApp
                  el-tag(type="info" size="mini" style="margin-left: 8px") PRÓXIMAMENTE
                .patient-trigger-row__help Reservado para próxima fase.

      //- ── TAB CONFIGURACIÓN IA (read-only) ──
      el-tab-pane(label="Configuración IA" name="ai")
        .ai-tab
          //- Header con plantilla + link
          .ai-tab__header(v-if="selectedTemplate")
            span.ai-tab__header-label Resuelto desde plantilla:
            el-tag(type="primary") {{ selectedTemplate.name }}
            router-link.ai-tab__edit-link(:to="{ name: 'whatsapp-config' }")
              | Editar la plantilla base →

          //- Vacío
          .ai-tab__empty(v-if="!selectedTemplate")
            i.el-icon-info
            span Elegí una plantilla en el tab General para ver los prompts.

          //- Prompts
          template(v-if="selectedTemplate")
            .ai-tab__prompt
              .ai-tab__prompt-header
                span.ai-tab__prompt-title Prompt conductor (cada turno)
                el-tag(type="info" size="mini") {{ conductorModelLabel }}
              el-input(
                type="textarea"
                :rows="10"
                :value="resolvedConductorPrompt"
                readonly
                resize="none"
              )
              .ai-tab__prompt-foot 🔒 Read-only. Para editar, modificá la plantilla base.

            .ai-tab__prompt
              .ai-tab__prompt-header
                span.ai-tab__prompt-title Prompt evaluador (al cierre)
                el-tag(type="info" size="mini") {{ evaluatorModelLabel }}
              el-input(
                type="textarea"
                :rows="10"
                :value="resolvedEvaluatorPrompt"
                readonly
                resize="none"
              )
              .ai-tab__prompt-foot 🔒 Read-only.

            .ai-tab__snapshot-note
              i.el-icon-warning-outline
              span Cuando se dispare esta task, se guardará una copia exacta de estos prompts. Cambios futuros a la plantilla NO afectan tasks ya disparadas.
</template>

<style lang="scss" scoped>
.task-health-conversation-form {
  font-family: "Plus Jakarta Sans", sans-serif;
}

.form-help {
  font-size: 11px;
  color: #6b7280;
  margin-top: 4px;
}

.criteria-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  .el-tag {
    margin: 0;
  }
}

.thresholds-row {
  display: flex;
  gap: 16px;
  align-items: flex-end;
  &__col {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
  }
  &__label {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
  }
  &__hint {
    font-size: 11px;
    color: #9ca3af;
  }
  &__readonly {
    height: 32px;
    display: inline-flex;
    align-items: center;
    padding: 0 12px;
    background: #fbe9e7;
    color: #b3261e;
    border-radius: 4px;
    font-weight: 600;
    font-size: 13px;
  }
}

.patient-trigger-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  &__text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  &__label {
    font-size: 13px;
    color: #374151;
  }
  &__help {
    font-size: 11px;
    color: #6b7280;
  }
}

.ai-tab {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
  }
  &__header-label {
    color: #6b7280;
  }
  &__edit-link {
    margin-left: auto;
    color: #1d3557;
    font-weight: 600;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
  &__empty {
    padding: 24px;
    text-align: center;
    color: #9ca3af;
    font-size: 13px;
    border: 1px dashed #e5e7eb;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  &__prompt {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  &__prompt-header {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__prompt-title {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
  }
  &__prompt-foot {
    font-size: 11px;
    color: #6b7280;
  }
  &__snapshot-note {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 12px;
    color: #6b7280;
    padding: 8px 12px;
    background: #f7f9fc;
    border-radius: 6px;
  }
}
</style>
